import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Header from './Header';
import { bienestarService } from '../services/api';
import './BienestarReservarPage.css';

const FULL_FREE_SLUGS = new Set(['yoga-1a-1', 'entrenamiento-1a-1', 'conecta-sesion']);

function readQuery() {
  const params = new URLSearchParams(window.location.search);
  return {
    servicio: params.get('servicio') || 'yoga-1a-1',
    email: params.get('email') || '',
  };
}

function formatScheduledLabel(isoLocal, locale) {
  if (!isoLocal) return '';
  const d = new Date(isoLocal.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return isoLocal;
  return d.toLocaleString(locale === 'en' ? 'en-CO' : 'es-CO', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'America/Bogota',
  });
}

const BienestarReservarPage = () => {
  const { t, i18n } = useTranslation();
  const initial = useMemo(() => readQuery(), []);

  const [step, setStep] = useState(1);
  const [catalog, setCatalog] = useState([]);
  const [serviceSlug, setServiceSlug] = useState(initial.servicio);
  const [email, setEmail] = useState(initial.email);
  const [couponCode, setCouponCode] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [identificationType, setIdentificationType] = useState('CC');
  const [identificationNumber, setIdentificationNumber] = useState('');
  const [customerNotes, setCustomerNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [bookingRef, setBookingRef] = useState(null);
  const [availability, setAvailability] = useState([]);
  const [selectedDay, setSelectedDay] = useState('');
  const [selectedSlot, setSelectedSlot] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    bienestarService.getCatalog()
      .then((res) => {
        const list = res?.services || res?.data?.services || [];
        setCatalog(Array.isArray(list) ? list : []);
      })
      .catch(() => setCatalog([]));
  }, []);

  const selectedService = useMemo(
    () => catalog.find((s) => s.slug === serviceSlug),
    [catalog, serviceSlug]
  );

  const isFullFree = FULL_FREE_SLUGS.has(serviceSlug);

  const daySlots = useMemo(() => {
    const day = availability.find((d) => d.date === selectedDay);
    return day?.slots?.filter((s) => s.available) || [];
  }, [availability, selectedDay]);

  const loadAvailability = useCallback(async (slug) => {
    setLoading(true);
    setError(null);
    try {
      const res = await bienestarService.getAvailability(slug);
      const data = res?.data || res;
      const days = data?.days || [];
      setAvailability(days);
      if (days.length > 0) {
        setSelectedDay(days[0].date);
      }
    } catch {
      setError(t('bienestarBooking.errorGeneric'));
      setAvailability([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (step === 3 && serviceSlug) {
      loadAvailability(serviceSlug);
    }
  }, [step, serviceSlug, loadAvailability]);

  const goBienestar = useCallback(() => {
    window.history.pushState({}, '', '/servicios#bienestar-experiencias');
    window.dispatchEvent(new PopStateEvent('popstate'));
  }, []);

  const handleRequestCoupon = async (e) => {
    e.preventDefault();
    if (!email.trim() || !serviceSlug) return;
    if (!isFullFree) {
      setError(t('bienestarBooking.programContactOnly'));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await bienestarService.requestWelcomeCoupon(email.trim(), serviceSlug);
      const data = res?.data || res;
      if (data?.code) {
        setCouponCode(data.code);
        setStep(2);
        window.history.replaceState(
          {},
          '',
          `/bienestar/reservar?servicio=${encodeURIComponent(serviceSlug)}&email=${encodeURIComponent(email.trim())}`
        );
      } else {
        setError(data?.message || t('bienestarBooking.errorGeneric'));
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || t('bienestarBooking.errorGeneric');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await bienestarService.confirmBooking({
        email: email.trim(),
        code: couponCode.trim(),
        service: serviceSlug,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim(),
        identificationType,
        identificationNumber: identificationNumber.trim(),
        customerNotes: customerNotes.trim(),
      });
      const data = res?.data || res;
      const ref = data?.booking?.bookingRef || data?.bookingRef;
      if (!ref) {
        setError(t('bienestarBooking.errorGeneric'));
        return;
      }
      setBookingRef(ref);
      setStep(3);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || t('bienestarBooking.errorGeneric');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleSchedule = async (e) => {
    e.preventDefault();
    if (!selectedSlot || !bookingRef) return;
    setLoading(true);
    setError(null);
    try {
      const res = await bienestarService.scheduleBooking({
        bookingRef,
        email: email.trim(),
        slotStart: selectedSlot,
      });
      const data = res?.data || res;
      const at = data?.booking?.scheduledAt || selectedSlot;
      setScheduledAt(at);
      setStep(4);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || t('bienestarBooking.errorGeneric');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const scheduledLabel = formatScheduledLabel(scheduledAt, i18n.language);

  return (
    <div className="br-page">
      <Header />
      <main className="br-main container">
        <button type="button" className="br-back" onClick={goBienestar}>
          <i className="fas fa-arrow-left" aria-hidden="true" /> {t('bienestarBooking.back')}
        </button>

        <header className="br-header">
          <p className="br-kicker">{t('bienestarBooking.kicker')}</p>
          <h1 className="br-title">{t('bienestarBooking.title')}</h1>
          <p className="br-lead">{t('bienestarBooking.lead')}</p>
        </header>

        <div className="br-steps" aria-hidden="true">
          {[1, 2, 3, 4].map((n, i) => (
            <React.Fragment key={n}>
              {i > 0 && <span className="br-step-line" />}
              <span className={step >= n ? 'br-step br-step--on' : 'br-step'}>{n}</span>
            </React.Fragment>
          ))}
        </div>

        {step === 1 && (
          <form className="br-card" onSubmit={handleRequestCoupon}>
            <h2>{t('bienestarBooking.step1Title')}</h2>
            <label className="br-label">
              <span>{t('bienestarBooking.serviceLabel')}</span>
              <select value={serviceSlug} onChange={(e) => setServiceSlug(e.target.value)} required>
                {catalog.length === 0 && (
                  <>
                    <option value="yoga-1a-1">Yoga 1 a 1</option>
                    <option value="entrenamiento-1a-1">Entrenamiento 1 a 1</option>
                    <option value="conecta-sesion">Conecta — sesión única</option>
                  </>
                )}
                {catalog.map((s) => (
                  <option key={s.slug} value={s.slug}>{s.name}</option>
                ))}
              </select>
            </label>
            {!isFullFree && (
              <p className="br-note">{t('bienestarBooking.programContactOnly')}</p>
            )}
            <label className="br-label">
              <span>{t('bienestarBooking.emailLabel')}</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@correo.com"
                required
                autoComplete="email"
              />
            </label>
            <p className="br-hint">{t('bienestarBooking.step1Hint')}</p>
            {error && <p className="br-error" role="alert">{error}</p>}
            <button type="submit" className="br-cta" disabled={loading || !isFullFree}>
              {loading ? '...' : t('bienestarBooking.step1Cta')}
            </button>
          </form>
        )}

        {step === 2 && (
          <form className="br-card" onSubmit={handleConfirm}>
            <h2>{t('bienestarBooking.step2Title')}</h2>
            {selectedService && (
              <p className="br-service-pill">{selectedService.name}</p>
            )}
            <div className="br-coupon-box">
              <span className="br-coupon-label">{t('bienestarBooking.couponLabel')}</span>
              <code className="br-coupon-code">{couponCode}</code>
            </div>
            <div className="br-row">
              <label className="br-label">
                <span>{t('bienestarBooking.firstName')}</span>
                <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
              </label>
              <label className="br-label">
                <span>{t('bienestarBooking.lastName')}</span>
                <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} required />
              </label>
            </div>
            <label className="br-label">
              <span>{t('bienestarBooking.phone')}</span>
              <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </label>
            <div className="br-row">
              <label className="br-label br-label--short">
                <span>{t('bienestarBooking.idType')}</span>
                <select value={identificationType} onChange={(e) => setIdentificationType(e.target.value)}>
                  <option value="CC">CC</option>
                  <option value="CE">CE</option>
                  <option value="NIT">NIT</option>
                  <option value="PP">Pasaporte</option>
                </select>
              </label>
              <label className="br-label">
                <span>{t('bienestarBooking.idNumber')}</span>
                <input
                  type="text"
                  value={identificationNumber}
                  onChange={(e) => setIdentificationNumber(e.target.value)}
                  required
                />
              </label>
            </div>
            <label className="br-label">
              <span>{t('bienestarBooking.notes')}</span>
              <textarea
                rows={3}
                value={customerNotes}
                onChange={(e) => setCustomerNotes(e.target.value)}
                placeholder={t('bienestarBooking.notesPlaceholder')}
              />
            </label>
            <p className="br-hint">{t('bienestarBooking.step2Hint')}</p>
            {error && <p className="br-error" role="alert">{error}</p>}
            <button type="submit" className="br-cta" disabled={loading}>
              {loading ? '...' : t('bienestarBooking.step2Cta')}
            </button>
          </form>
        )}

        {step === 3 && (
          <form className="br-card" onSubmit={handleSchedule}>
            <h2>{t('bienestarBooking.step3Title')}</h2>
            <p className="br-hint">{t('bienestarBooking.step3Hint')}</p>
            {bookingRef && (
              <p className="br-ref-inline">{t('bienestarBooking.refLabel')}: <strong>{bookingRef}</strong></p>
            )}

            {loading && availability.length === 0 ? (
              <p className="br-hint">{t('bienestarBooking.loadingSlots')}</p>
            ) : availability.length === 0 ? (
              <p className="br-note">{t('bienestarBooking.noSlots')}</p>
            ) : (
              <>
                <div className="br-day-picker" role="tablist" aria-label={t('bienestarBooking.pickDay')}>
                  {availability.map((day) => (
                    <button
                      key={day.date}
                      type="button"
                      role="tab"
                      aria-selected={selectedDay === day.date}
                      className={selectedDay === day.date ? 'br-day-btn br-day-btn--on' : 'br-day-btn'}
                      onClick={() => { setSelectedDay(day.date); setSelectedSlot(''); }}
                    >
                      {day.label}
                    </button>
                  ))}
                </div>

                <div className="br-slot-grid">
                  {daySlots.map((slot) => (
                    <button
                      key={slot.start}
                      type="button"
                      className={selectedSlot === slot.start ? 'br-slot br-slot--on' : 'br-slot'}
                      onClick={() => setSelectedSlot(slot.start)}
                    >
                      {slot.label}
                    </button>
                  ))}
                </div>
                {daySlots.length === 0 && selectedDay && (
                  <p className="br-note">{t('bienestarBooking.noSlotsDay')}</p>
                )}
              </>
            )}

            {error && <p className="br-error" role="alert">{error}</p>}
            <button type="submit" className="br-cta" disabled={loading || !selectedSlot}>
              {loading ? '...' : t('bienestarBooking.step3Cta')}
            </button>
          </form>
        )}

        {step === 4 && (
          <div className="br-card br-success-card">
            <i className="fas fa-check-circle br-success-icon" aria-hidden="true" />
            <h2>{t('bienestarBooking.successTitle')}</h2>
            {bookingRef && (
              <p className="br-ref">{t('bienestarBooking.refLabel')}: <strong>{bookingRef}</strong></p>
            )}
            {scheduledLabel && (
              <p className="br-scheduled">
                <i className="fas fa-calendar-check" aria-hidden="true" /> {scheduledLabel}
              </p>
            )}
            <p>{t('bienestarBooking.successBody')}</p>
            <button
              type="button"
              className="br-cta br-cta--secondary"
              onClick={() => {
                const q = new URLSearchParams({ ref: bookingRef || '', email: email || '' });
                window.history.pushState({}, '', `/bienestar/reprogramar?${q.toString()}`);
                window.dispatchEvent(new PopStateEvent('popstate'));
              }}
            >
              {t('bienestarBooking.rescheduleCta')}
            </button>
            <button type="button" className="br-cta" onClick={goBienestar}>
              {t('bienestarBooking.backBienestar')}
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default BienestarReservarPage;
